/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { Calendar, CalendarDays, CheckCircle, Target, TrendingUp, Trophy, Users } from 'lucide-react';
import React from 'react';
import { TournamentAnalyticsSummary } from 'modules/analytics/domain/interfaces/tournament-analytics';
import KpiCard from './kpi-card';

interface KpiGridProps {
  summary: TournamentAnalyticsSummary | undefined;
  loading: boolean;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const SkeletonCard = styled.div`
  background: ${({ theme }) => theme.colors.SurfaceCard};
  border: 1px solid ${({ theme }) => theme.colors.LineSubtle};
  border-radius: ${({ theme }) => theme.radii.panel};
  height: 92px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const ICON_SIZE = 22;

const KpiGrid = React.memo(({ summary, loading }: KpiGridProps) => {
  const { t } = useLingui();

  if (loading || !summary) {
    return (
      <Grid>
        {Array.from({ length: 7 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </Grid>
    );
  }

  const playedRatio = summary.matchesCount > 0 ? summary.playedMatchesCount / summary.matchesCount : 0;

  return (
    <Grid>
      <KpiCard icon={<Trophy size={ICON_SIZE} />} value={summary.championshipsCount} label={t`Чемпионатов`} tone="gold" />
      <KpiCard icon={<Calendar size={ICON_SIZE} />} value={summary.matchesCount} label={t`Матчей`} tone="blue" />
      <KpiCard
        icon={<CheckCircle size={ICON_SIZE} />}
        value={summary.playedMatchesCount}
        label={t`Сыгранных матчей`}
        tone="green"
        progress={playedRatio}
        progressHint={`${summary.playedMatchesCount} / ${summary.matchesCount}`}
      />
      <KpiCard icon={<Users size={ICON_SIZE} />} value={summary.uniqueTeamsCount} label={t`Уникальных команд`} tone="blue" />
      <KpiCard icon={<Target size={ICON_SIZE} />} value={summary.totalGoals} label={t`Голов забито`} tone="amber" />
      <KpiCard
        icon={<TrendingUp size={ICON_SIZE} />}
        value={summary.averageGoalsPerMatch}
        label={t`Среднее голов за матч`}
        tone="amber"
      />
      <KpiCard icon={<CalendarDays size={ICON_SIZE} />} value={summary.gameDaysCount} label={t`Игровых дней`} tone="gold" />
    </Grid>
  );
});

export default KpiGrid;
